import React from 'react';
import CustomMedia from 'util/custom-media-query';
import i18n from 'i18n';
import Image from 'components/common/Image';
import { applyStyles } from 'util/helpers';
import { required, composeValidators, surrogatePairPattern } from 'validation/validators';
import FontStyles from 'constants/font-styles';
import styled from 'styled-components';
import Colors from 'constants/colors';
import Button from 'components/common/Button';
import SelectField from 'components/common/SelectField';
import TextField from 'components/common/TextField';
import InscriptionType from 'constants/inscription-type';
import imgInscriptionKotobukiPC1x from 'assets/images/PC/@1x/img_Inscription_kotobuki.png';
import imgInscriptionKotobukiPC2x from 'assets/images/PC/@2x/img_Inscription_kotobuki@2x.png';
import imgInscriptionKotobukiSP2x from 'assets/images/SP/@2x/img_Inscription_kotobuki@2x.png';
import imgInscriptionKotobukiSP3x from 'assets/images/SP/@3x/img_Inscription_kotobuki@3x.png';
import imgInscriptionSyukuPC1x from 'assets/images/PC/@1x/img_Inscription_syuku.png';
import imgInscriptionSyukuPC2x from 'assets/images/PC/@2x/img_Inscription_syuku@2x.png';
import imgInscriptionSyukuSP2x from 'assets/images/SP/@2x/img_Inscription_syuku@2x.png';
import imgInscriptionSyukuSP3x from 'assets/images/SP/@3x/img_Inscription_syuku@3x.png';
import imgInscriptionFree1PC1x from 'assets/images/PC/@1x/img_Inscription_free1.png';
import imgInscriptionFree1PC2x from 'assets/images/PC/@2x/img_Inscription_free1@2x.png';
import imgInscriptionFree1SP2x from 'assets/images/SP/@2x/img_Inscription_free1@2x.png';
import imgInscriptionFree1SP3x from 'assets/images/SP/@3x/img_Inscription_free1@3x.png';
import imgInscriptionFree2PC1x from 'assets/images/PC/@1x/img_Inscription_free2.png';
import imgInscriptionFree2PC2x from 'assets/images/PC/@2x/img_Inscription_free2@2x.png';
import imgInscriptionFree2SP2x from 'assets/images/SP/@2x/img_Inscription_free2@2x.png';
import imgInscriptionFree2SP3x from 'assets/images/SP/@3x/img_Inscription_free2@3x.png';
import imgInscriptionName1PC1x from 'assets/images/PC/@1x/img_Inscription_name1.png';
import imgInscriptionName1PC2x from 'assets/images/PC/@2x/img_Inscription_name1@2x.png';
import imgInscriptionName1SP2x from 'assets/images/SP/@2x/img_Inscription_name1@2x.png';
import imgInscriptionName1SP3x from 'assets/images/SP/@3x/img_Inscription_name1@3x.png';
import imgInscriptionName2PC1x from 'assets/images/PC/@1x/img_Inscription_name2.png';
import imgInscriptionName2PC2x from 'assets/images/PC/@2x/img_Inscription_name2@2x.png';
import imgInscriptionName2SP2x from 'assets/images/SP/@2x/img_Inscription_name2@2x.png';
import imgInscriptionName2SP3x from 'assets/images/SP/@3x/img_Inscription_name2@3x.png';
import imgInscriptionNonePC1x from 'assets/images/PC/@1x/img_Inscription_none.png';
import imgInscriptionNonePC2x from 'assets/images/PC/@2x/img_Inscription_none@2x.png';
import imgInscriptionNoneSP2x from 'assets/images/SP/@2x/img_Inscription_none@2x.png';
import imgInscriptionNoneSP3x from 'assets/images/SP/@3x/img_Inscription_none@3x.png';

const inscriptionOptions = [
  {
    label: i18n.t('orderAggregators:common:order.kotobuki'),
    value: i18n.t('orderAggregators:common:order.kotobuki')
  },
  {
    label: i18n.t('orderAggregators:common:order.iwai'),
    value: i18n.t('orderAggregators:common:order.iwai')
  },
  {
    label: i18n.t('orderAggregators:common:order.freeWord1'),
    value: InscriptionType.oneLine
  },
  {
    label: i18n.t('orderAggregators:common:order.freeWord2'),
    value: InscriptionType.twoLines
  },
  {
    label: i18n.t('common:none'),
    value: InscriptionType.none
  }
];

const shipperNameOptions = [
  {
    label: i18n.t('orderAggregators:common:order.freeWord1'),
    value: InscriptionType.oneLine
  },
  {
    label: i18n.t('orderAggregators:common:order.freeWord2'),
    value: InscriptionType.twoLines
  },
  {
    label: i18n.t('common:none'),
    value: InscriptionType.none
  }
];

const FreeWordField = ({ name, label }) => (
  <div className="free-word">
    <TextField
      name={name}
      label={label}
      maxLength={10}
      validate={value => composeValidators(required, surrogatePairPattern)(value)}
    />
    <span className="notes">
      {i18n.t('orderAggregators:common:order.freeWordNotes')}
    </span>
  </div>
);

const FreeWordFields = ({ name, value, labelPrefix }) => (
  <div className="free-words">
    {value === InscriptionType.oneLine && (
      <FreeWordField
        name={`${name}[0]`}
        label={i18n.t(
          `orderAggregators:common:order.${labelPrefix}FreeWordLabel`
        )}
      />
    )}
    {value === InscriptionType.twoLines && (
      <React.Fragment>
        <FreeWordField
          name={`${name}[0]`}
          label={i18n.t(
            `orderAggregators:common:order.${labelPrefix}FreeWordLabel1`
          )}
        />
        <FreeWordField
          name={`${name}[1]`}
          label={i18n.t(
            `orderAggregators:common:order.${labelPrefix}FreeWordLabel2`
          )}
        />
      </React.Fragment>
    )}
  </div>
);

const InscriptionUpperImage = value => {
  switch (value) {
    case i18n.t('orderAggregators:common:order.kotobuki'):
      return (
        <Image
          pc1x={imgInscriptionKotobukiPC1x}
          pc2x={imgInscriptionKotobukiPC2x}
          sp2x={imgInscriptionKotobukiSP2x}
          sp3x={imgInscriptionKotobukiSP3x}
          alt="img_inscription_kotobuki"
        />
      );

    case i18n.t('orderAggregators:common:order.iwai'):
      return (
        <Image
          pc1x={imgInscriptionSyukuPC1x}
          pc2x={imgInscriptionSyukuPC2x}
          sp2x={imgInscriptionSyukuSP2x}
          sp3x={imgInscriptionSyukuSP3x}
          alt="img_inscription_syuku"
        />
      );

    case InscriptionType.oneLine:
      return (
        <Image
          pc1x={imgInscriptionFree1PC1x}
          pc2x={imgInscriptionFree1PC2x}
          sp2x={imgInscriptionFree1SP2x}
          sp3x={imgInscriptionFree1SP3x}
          alt="img_inscription_free1"
        />
      );

    case InscriptionType.twoLines:
      return (
        <Image
          pc1x={imgInscriptionFree2PC1x}
          pc2x={imgInscriptionFree2PC2x}
          sp2x={imgInscriptionFree2SP2x}
          sp3x={imgInscriptionFree2SP3x}
          alt="img_inscription_free2"
        />
      );

    default:
      return (
        <Image
          pc1x={imgInscriptionNonePC1x}
          pc2x={imgInscriptionNonePC2x}
          sp2x={imgInscriptionNoneSP2x}
          sp3x={imgInscriptionNoneSP3x}
          alt="img_inscription_none"
        />
      );
  }
};

const InscriptionShipperImage = value => {
  switch (value) {
    case InscriptionType.oneLine:
      return (
        <Image
          pc1x={imgInscriptionName1PC1x}
          pc2x={imgInscriptionName1PC2x}
          sp2x={imgInscriptionName1SP2x}
          sp3x={imgInscriptionName1SP3x}
          alt="img_inscription_name1"
        />
      );

    case InscriptionType.twoLines:
      return (
        <Image
          pc1x={imgInscriptionName2PC1x}
          pc2x={imgInscriptionName2PC2x}
          sp2x={imgInscriptionName2SP2x}
          sp3x={imgInscriptionName2SP3x}
          alt="img_inscription_name2"
        />
      );

    default:
      return (
        <Image
          pc1x={imgInscriptionNonePC1x}
          pc2x={imgInscriptionNonePC2x}
          sp2x={imgInscriptionNoneSP2x}
          sp3x={imgInscriptionNoneSP3x}
          alt="img_inscription_none"
        />
      );
  }
};

const DialogBodyTemplate = ({ className, form }) => {
  const formValues = form.getState().values;

  return (
    <div className={className}>
      <div className="inscription-container">
        <div className="inscription-upper">
          <label className="inscription-label">
            {i18n.t('orderAggregators:common:order.inscriptionLabel')}
          </label>
          <div className="inscription">
            {InscriptionUpperImage(formValues.inscriptionType)}
            <div className="fields-container">
              <SelectField
                name="inscriptionType"
                options={inscriptionOptions}
              />
              <FreeWordFields
                name="inscription"
                value={formValues.inscriptionType}
                labelPrefix="inscription"
              />
            </div>
          </div>
        </div>
        <div className="shipper-name">
          <label className="inscription-label">
            {i18n.t('orderAggregators:common:order.shipperNameLabel')}
          </label>
          <div className="inscription">
            {InscriptionShipperImage(formValues.shipperNameType)}
            <div className="fields-container">
              <SelectField
                name="shipperNameType"
                options={shipperNameOptions}
              />
              <FreeWordFields
                name="shipperName"
                value={formValues.shipperNameType}
                labelPrefix="shipperName"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const bodyPCStyle = CustomMedia.greaterThan('umedium')`
  height: 100%;

  .inscription-container {
    height: 100%;
    overflow: scroll;
    padding: 32px;
  }

  .inscription-upper {
    margin-bottom: 32px;
  }

  .inscription-label {
    ${FontStyles.text5};

    margin-bottom: 16px;
    color: ${Colors.black1};
  }

  .inscription {
    display: flex;
  }

  img {
    max-width: 100%;
  }

  .fields-container {
    width: 508px;
    margin-left: 16px;
  }

  ${SelectField},
  ${TextField} {
    max-width: 226px;
    margin: 8px 0;

    label {
      ${FontStyles.text10};

      margin-bottom: 6px;
    }
  }

  .free-words {
    display: flex;
  }

  .free-word {
    width: 50%;

    &:first-child {
      margin-right: 16px;
    }
  }

  .notes {
    ${FontStyles.text12};
  }
`;

const bodySPStyle = CustomMedia.lessThan('medium')`
  padding: 16px 16px 0;

  .inscription-container {
    height: calc(100% - 64px);
    overflow: scroll;
  }

  .inscription-upper,
  .shipper-name {
    margin-bottom: 32px;
  }

  .inscription-label {
    ${FontStyles.text5};

    display: block;
    color: ${Colors.black1};
    margin-bottom: 16px;
  }

  .inscription {
    display: flex;
  }

  .fields-container {
    width: 508px;
    margin-left: 16px;
  }

  ${SelectField},
  ${TextField} {
    max-width: 226px;
    padding: 0;
    margin: 0;

    label {
      ${FontStyles.text7};

      margin-bottom: 12px;
    }
  }

  .free-word {
    width: 100%;
    margin-top: 24px;
  }

  .notes {
    ${FontStyles.text8};

    display: inline-block;
    margin-top: 8px;
  }
`;

export const DialogBody = applyStyles(DialogBodyTemplate)(
  bodyPCStyle,
  bodySPStyle
);

const DialogFooterTemplate = ({
  className,
  invalid,
  handleSubmit,
  onClickBack
}) => (
  <div className={className}>
    <Button buttonType="D" type="button" onClick={onClickBack}>
      {i18n.t('orderAggregators:common:order.inscriptionBack')}
    </Button>
    <Button
      buttonType="B"
      type="button"
      onClick={handleSubmit}
      disabled={invalid}
    >
      {i18n.t('common:save')}
    </Button>
  </div>
);

export const DialogFooter = styled(DialogFooterTemplate)`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;

  ${Button}:first-child {
    margin-right: 16px;
  }
`;



// WEBPACK FOOTER //
// ./src/components/OrderAggregators/components/InscriptionDialogComponents.js